import { useRouter } from "next/router";

/**
 * Custom hook to retrieve the current locale from Next.js router.
 *
 * This hook abstracts the logic of accessing the locale from useRouter(),
 * ensuring that it is only used within React components following the rules of hooks.
 *
 * @returns {string} The current locale string (e.g., "en-US", "es-ES").
 */
export const useLocale = () => {
  const { locale } = useRouter();
  return locale ?? "en-US";
};

/**
 * Custom hook to retrieve the user's country code from the Next.js router locale.
 *
 * This hook extracts the country code from the locale string (e.g., "en-US" → "US"),
 * ensuring it follows the correct format. If the locale is not available,
 * it defaults to "US".
 *
 * @returns {string} The extracted uppercase country code (e.g., "US", "CA", "FR").
 *                   Defaults to "US" if the locale is undefined.
 */
export const useUserCountry = () => {
  const { locale } = useRouter();
  return locale?.split("-").pop()?.toUpperCase() ?? "US";
};
